import { useState, useEffect } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { db } from '../firebase';

// ==========================================
// ⚙️ FLEET CONFIGURATION SETTINGS
// ==========================================
export const MAX_TANK_VOLUME = 20000; // 20L = 20000ml
export const ALERT_THRESHOLD = 10000; // Warning at ≤ 10L
// ==========================================

// Shared "today" cutoff for the fleet — the business operates in India, and
// order timestamps elsewhere (TransactionsTable) already assume IST, so
// "today" means the current calendar day in Asia/Kolkata (UTC+5:30), not UTC.
// Returns unix seconds, matching order.timestamp's format.
export function getISTMidnightTimestamp() {
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const istNow = new Date(Date.now() + IST_OFFSET_MS);
  const istMidnightAsUTC = Date.UTC(istNow.getUTCFullYear(), istNow.getUTCMonth(), istNow.getUTCDate());
  return Math.floor((istMidnightAsUTC - IST_OFFSET_MS) / 1000);
}

export function useFirebaseData(user) {
  const [inventory, setInventory] = useState({});
  const [orders, setOrders] = useState([]);
  const [refundedIds, setRefundedIds] = useState(new Set());
  const [totalCups, setTotalCups] = useState(0);
  const [revenue, setRevenue] = useState(0);
  const [telemetry, setTelemetry] = useState({});
  const [tempHistory, setTempHistory] = useState({});
  const [refundQueue, setRefundQueue] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setInventory({});
      setOrders([]);
      setRefundedIds(new Set());
      setTotalCups(0);
      setRevenue(0);
      setTelemetry({});
      setTempHistory({});
      setLoading(false);
      return;
    }

    setLoading(true);

    const inventoryRef    = ref(db, 'inventory');
    const ordersRef       = ref(db, 'orders');
    const telemetryRef    = ref(db, 'telemetry');
    const refundQueueRef  = ref(db, 'refund_queue');

    const unsubInventory = onValue(inventoryRef, (snapshot) => {
      if (snapshot.val()) setInventory(snapshot.val());
    }, (error) => console.error('Inventory listener error:', error));

    // Only fully-REFUNDED orders are hidden by default.
    // REFUND_PENDING orders stay visible in the table with a yellow badge.
    const unsubRefundQueue = onValue(refundQueueRef, (snapshot) => {
      const data = snapshot.val() || {};
      setRefundQueue(data);  // full data for RefundQueuePanel
      const refunded = new Set(
        Object.entries(data)
          .filter(([, v]) => v?.status === 'REFUNDED')
          .map(([id]) => id)
      );
      setRefundedIds(refunded);
    }, (error) => console.error('RefundQueue listener error:', error));


    const unsubOrders = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const orderArray = Object.entries(data).map(([id, details]) => ({
          id,
          ...(typeof details === 'object' ? details : { status: details })
        }));
        orderArray.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setOrders(orderArray);
      }
      setLoading(false);
    }, (error) => {
      console.error('Orders listener error:', error);
      setLoading(false);
    });

    // Live telemetry: temperature + heartbeat from each Pi
    const unsubTelemetry = onValue(telemetryRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setTelemetry(data);
        // Build tempHistory: { kioskId: { tsKey: { t, ts }, ... }, ... }
        const hist = {};
        Object.entries(data).forEach(([kioskId, kData]) => {
          if (kData.history) hist[kioskId] = kData.history;
        });
        setTempHistory(hist);
      }
    }, (error) => console.error('Telemetry listener error:', error));

    return () => {
      unsubInventory();
      unsubOrders();
      unsubTelemetry();
      unsubRefundQueue();
    };
  }, [user]);

  // Recalculate revenue/cups whenever orders or refundedIds change
  useEffect(() => {
    let calcRevenue = 0;
    let calcCups = 0;
    orders.forEach(order => {
      // Skip refunded orders from KPI totals
      if (refundedIds.has(order.id)) return;
      if (order.amount) calcRevenue += Number(order.amount);
      if (order.item_id) calcCups += 1;
      if (!order.amount && order.status === 'PAID') {
        calcRevenue += 19;
        calcCups += 1;
      }
    });
    setRevenue(calcRevenue);
    setTotalCups(calcCups);
  }, [orders, refundedIds]);

  const restockJuice = (kioskId, juiceId, cleanName) => {
    const isConfirmed = window.confirm(
      `Confirm: Refill ${cleanName} at ${kioskId} to full capacity (${MAX_TANK_VOLUME}ml)?`
    );
    if (isConfirmed) {
      const targetRef = ref(db, `inventory/${kioskId}/${juiceId}`);
      set(targetRef, MAX_TANK_VOLUME)
        .catch(() => alert('Failed to update database. Check permissions.'));
    }
  };

  return { inventory, orders, refundedIds, refundQueue, totalCups, revenue, telemetry, tempHistory, loading, restockJuice };
}
